/**
 * AbilityVFX — one-shot particle bursts hooked to ability events.
 *
 * Listens on the typed EventBus for ability firings and emits short-lived
 * particle systems at the relevant world position. Used by every level via
 * BaseLevel, so every ability gains visible feedback without per-level
 * wiring.
 *
 * Design notes:
 *   - Each burst gets its own ParticleSystem instance with targetStopDuration
 *     set so Babylon cleans it up automatically.
 *   - Sprites are drawn into a single DynamicTexture once (soft radial gradient)
 *     and shared across all bursts — no asset file dependency.
 *   - Positions come from event payloads where available, else from the player
 *     mesh passed to the constructor.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { AccessibilitySystem } from "./AccessibilitySystem";
import { particleDensityFactor } from "./particleScaling";
import { a11yColor4, a11yColor4FromRgb } from "./abilityColorHelpers";

/** Shared radial-gradient sprite so we don't redraw per burst. */
function makeSoftSprite(scene: Scene): Texture {
  const size = 64;
  const dt = new DynamicTexture("abilityVfxSprite", size, scene, false);
  const ctx = dt.getContext();
  const g = ctx.createRadialGradient(size / 2, size / 2, 1, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.5, "rgba(255,255,255,0.55)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  dt.update(false);
  dt.hasAlpha = true;
  return dt;
}

export interface AbilityVFXVisualConfig {
  /** Omit the extra decorative dome when the ability owns its enclosure. */
  chronosSphere?: boolean;
  particleDensityScale?: number;
  particleSizeScale?: number;
}

interface BurstSpec {
  emitter: Vector3;
  /** Number of particles to spawn (approximate). */
  count: number;
  /** How long the emitter runs before auto-stopping. Seconds. */
  durationSec: number;
  /** Colors (Color4: rgba with alpha). */
  color1: Color4;
  color2: Color4;
  colorDead: Color4;
  minSize: number;
  maxSize: number;
  minLifeTime: number;
  maxLifeTime: number;
  minEmitPower: number;
  maxEmitPower: number;
  /** Emission cone half-angle. 0 = outward radial sphere. */
  direction1: Vector3;
  direction2: Vector3;
  gravity?: Vector3;
  blendMode?: number;
  emitRate: number;
}

export class AbilityVFX {
  private scene: Scene;
  private minna: AbstractMesh;
  private basil: AbstractMesh;
  private sprite: Texture;
  private unsubs: Array<() => void> = [];
  private disposed = false;
  private nextPurrAccentAt = -Infinity;
  /** Active one-shot systems awaiting auto-dispose. */
  private pool: ParticleSystem[] = [];
  /** Active transient dynamic PointLights capped at 4 to stay within WebGL uniform budgets. */
  private activeLights: Array<{ light: PointLight; cleanup: () => void }> = [];
  private readonly transientCleanups = new Set<() => void>();

  constructor(scene: Scene, minna: AbstractMesh, basil: AbstractMesh,
    private readonly visualConfig: AbilityVFXVisualConfig = {}) {
    this.scene = scene;
    this.minna = minna;
    this.basil = basil;
    this.sprite = makeSoftSprite(scene);
    this.wireEvents();
  }

  private wireEvents(): void {
    // Basil — brass sparks on a successful Turn-Table Touch hit.
    this.unsubs.push(
      gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, ({ targetPosition, combo }) => {
        const emitter = new Vector3(targetPosition.x, targetPosition.y, targetPosition.z);
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        this.burst({
          emitter,
          count: 22 + Math.min(combo, 6) * 4,
          durationSec: 0.18,
          color1: a11yColor4FromRgb('BRASS', 1.0, 0.82, 0.4, 1.0, rc),
          color2: a11yColor4FromRgb('COPPER', 1.0, 0.62, 0.2, 1.0, rc),
          colorDead: new Color4(0.6, 0.3, 0.05, 0),
          minSize: 0.05,
          maxSize: 0.14,
          minLifeTime: 0.25,
          maxLifeTime: 0.45,
          minEmitPower: 2.2,
          maxEmitPower: 4.8,
          direction1: new Vector3(-1.2, 0.4, -0.6),
          direction2: new Vector3(1.2, 1.6, 0.6),
          gravity: new Vector3(0, -6, 0),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 240,
        });
        this.pulseLight(emitter, a11yColor4FromRgb('BRASS', 1.0, 0.82, 0.4, 1.0, rc), 2.2, 4.5, 220);
      }),
    );

    // Basil — red sputter on a miss so the player reads the failure quickly.
    this.unsubs.push(
      gameEvents.on(GameEvents.TURN_TABLE_TOUCH_MISS, ({ targetPosition }) => {
        const emitter = new Vector3(targetPosition.x, targetPosition.y, targetPosition.z);
        this.burst({
          emitter,
          count: 14,
          durationSec: 0.22,
          color1: new Color4(1.0, 0.25, 0.15, 1.0),
          color2: new Color4(0.6, 0.1, 0.08, 1.0),
          colorDead: new Color4(0.2, 0.02, 0.02, 0),
          minSize: 0.06,
          maxSize: 0.12,
          minLifeTime: 0.25,
          maxLifeTime: 0.45,
          minEmitPower: 1.2,
          maxEmitPower: 2.8,
          direction1: new Vector3(-0.6, 0.2, -0.3),
          direction2: new Vector3(0.6, 1.0, 0.3),
          gravity: new Vector3(0, -7, 0),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 160,
        });
      }),
    );

    // Basil — dust ring on Overclocked Leap landing / fire.
    this.unsubs.push(
      gameEvents.on(GameEvents.OVERCLOCKED_LEAP_FIRED, ({ position }) => {
        const emitter = new Vector3(position.x, position.y, position.z);
        this.burst({
          emitter,
          count: 40,
          durationSec: 0.3,
          color1: new Color4(0.82, 0.68, 0.44, 0.85),  // dust
          color2: new Color4(0.55, 0.4, 0.22, 0.75),
          colorDead: new Color4(0.25, 0.18, 0.1, 0),
          minSize: 0.08,
          maxSize: 0.24,
          minLifeTime: 0.45,
          maxLifeTime: 0.75,
          minEmitPower: 1.6,
          maxEmitPower: 3.4,
          direction1: new Vector3(-1.4, 0.2, -0.3),
          direction2: new Vector3(1.4, 0.8, 0.3),
          gravity: new Vector3(0, -4, 0),
          blendMode: ParticleSystem.BLENDMODE_STANDARD,
          emitRate: 300,
        });
        this.pulseLight(emitter, new Color4(0.95, 0.68, 0.32, 1.0), 2.5, 5.5, 300);
      }),
    );

    // Minna — expanding scan pulse when Aura Sight activates.
    // Kept subtle so it signals activation without drowning the HUD.
    this.unsubs.push(
      gameEvents.on(GameEvents.ABILITY_ACTIVATED, ({ abilityId }) => {
        if (abilityId !== "auraSight") return;
        const p = this.minna.position;
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        const auraOrigin = new Vector3(p.x, p.y + 0.4, p.z);
        const auraColor = a11yColor4FromRgb('MINNA_AURA', 0.15, 1.0, 0.6, 0.9, rc);
        this.burst({
          emitter: auraOrigin,
          count: 50,
          durationSec: 0.3,
          color1: auraColor,
          color2: a11yColor4FromRgb('BIOLUMINESCENT_BLUE', 0.4, 0.95, 1.0, 0.8, rc),
          colorDead: new Color4(0.02, 0.2, 0.15, 0),
          minSize: 0.05,
          maxSize: 0.14,
          minLifeTime: 0.45,
          maxLifeTime: 0.8,
          minEmitPower: 1.2,
          maxEmitPower: 2.6,
          direction1: new Vector3(-2.0, -0.4, -2.0),
          direction2: new Vector3(2.0, 0.8, 2.0),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 400,
        });
        this.pulseLight(auraOrigin, auraColor, 2.6, 7.5, 420);
      }),
    );

    // Basil — brass bracket snap sparks when Pneumatic Anchor activates.
    this.unsubs.push(
      gameEvents.on(GameEvents.ABILITY_ACTIVATED, ({ abilityId }) => {
        if (abilityId !== "pneumaticAnchor") return;
        const p = this.basil.position;
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        const anchorOrigin = new Vector3(p.x, p.y - 0.4, p.z);
        const brassColor = a11yColor4FromRgb('BRASS', 1.0, 0.78, 0.35, 1.0, rc);
        this.burst({
          emitter: anchorOrigin,
          count: 18,
          durationSec: 0.2,
          color1: brassColor,
          color2: a11yColor4FromRgb('COPPER', 0.9, 0.55, 0.25, 1.0, rc),
          colorDead: new Color4(0.3, 0.15, 0.05, 0),
          minSize: 0.06,
          maxSize: 0.12,
          minLifeTime: 0.25,
          maxLifeTime: 0.4,
          minEmitPower: 1.6,
          maxEmitPower: 3.2,
          direction1: new Vector3(-1.0, 0.1, -0.3),
          direction2: new Vector3(1.0, 0.6, 0.3),
          gravity: new Vector3(0, -10, 0),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 200,
        });
        this.pulseLight(anchorOrigin, brassColor, 2.0, 4.0, 240);
      }),
    );

    // Minna — soft warm ripple driven by the per-frame purr pulse so
    // the VFX tracks the pulse's actual location, not a debounced
    // transition event. NPC_CALMED stays semantic (one-shot per NPC).
    this.unsubs.push(
      gameEvents.on(GameEvents.PURR_PULSE, (payload) => {
        // PURR_PULSE is a fixed-step gameplay event. One-shot accents are
        // quarter-second pulses; they must not allocate a light/burst at60Hz.
        const now = Date.now();
        if (now < this.nextPurrAccentAt) return;
        this.nextPurrAccentAt = now + 250;
        const src = payload?.sourcePosition ?? this.minna.position;
        const purrOrigin = new Vector3(src.x, src.y + 0.2, src.z);
        this.burst({
          emitter: purrOrigin,
          count: 18,
          durationSec: 0.35,
          color1: new Color4(1.0, 0.9, 0.7, 0.7),
          color2: new Color4(0.8, 0.65, 0.9, 0.5),
          colorDead: new Color4(0.2, 0.18, 0.3, 0),
          minSize: 0.06,
          maxSize: 0.18,
          minLifeTime: 0.6,
          maxLifeTime: 1.0,
          minEmitPower: 0.4,
          maxEmitPower: 1.2,
          direction1: new Vector3(-1.5, -0.1, -1.5),
          direction2: new Vector3(1.5, 0.6, 1.5),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 90,
        });
        this.pulseLight(purrOrigin, new Color4(1.0, 0.88, 0.68, 1.0), 1.6, 5.5, 380, this.minna);
      }),
    );

    // Co-op — echoing green ring of particles on Resonant Drop.
    this.unsubs.push(
      gameEvents.on(GameEvents.RESONANT_DROP_TRIGGERED, (p) => {
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        // Emerald/Green shockwave ring
        this.burst({
          emitter: new Vector3(p.x, p.y, p.z),
          count: 80,
          durationSec: 0.45,
          color1: a11yColor4FromRgb('EMERALD', 0.0, 1.0, 0.4, 1.0, rc),
          color2: a11yColor4FromRgb('BIOLUMINESCENT_GREEN', 0.2, 0.8, 0.3, 0.8, rc),
          colorDead: new Color4(0.0, 0.2, 0.05, 0),
          minSize: 0.08,
          maxSize: 0.22,
          minLifeTime: 0.5,
          maxLifeTime: 0.95,
          minEmitPower: 3.5,
          maxEmitPower: 7.0,
          direction1: new Vector3(-2.5, 0.0, -2.5),
          direction2: new Vector3(2.5, 0.5, 2.5),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 420,
        });

        // Copper sparks burst (from Basil ground slam)
        this.burst({
          emitter: new Vector3(p.x, p.y, p.z),
          count: 50,
          durationSec: 0.3,
          color1: a11yColor4FromRgb('BRASS', 1.0, 0.7, 0.3, 1.0, rc),
          color2: a11yColor4FromRgb('COPPER', 0.9, 0.4, 0.1, 1.0, rc),
          colorDead: new Color4(0.3, 0.1, 0.02, 0),
          minSize: 0.05,
          maxSize: 0.15,
          minLifeTime: 0.3,
          maxLifeTime: 0.6,
          minEmitPower: 2.5,
          maxEmitPower: 5.5,
          direction1: new Vector3(-2.0, 0.2, -2.0),
          direction2: new Vector3(2.0, 1.5, 2.0),
          gravity: new Vector3(0, -9.81, 0),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 300,
        });
        this.pulseLight(
          new Vector3(p.x, p.y + 0.3, p.z),
          a11yColor4FromRgb('EMERALD', 0.0, 1.0, 0.4, 1.0, rc),
          3.8,
          10.0,
          520,
        );

        // Trigger lightweight camera shake (safe, non-game-loop-freezing)
        // Skipped entirely when reducedMotion is enabled (vestibular comfort).
        const reducedForShake = AccessibilitySystem.getInstance().state.reducedMotion;
        const camera: any = this.scene.activeCamera;
        if (!reducedForShake && camera && this.scene.getEngine().name !== "NullEngine") {
          const originalTarget = camera.getTarget().clone();
          let elapsed = 0;
          const duration = 350; // ms
          const intensity = 0.14;

          const shakeObs = this.scene.onBeforeRenderObservable.add(() => {
            const dt = this.scene.getEngine().getDeltaTime();
            elapsed += dt;
            if (elapsed < duration) {
              const offsetX = (Math.random() - 0.5) * intensity;
              const offsetY = (Math.random() - 0.5) * intensity;
              camera.setTarget(originalTarget.add(new Vector3(offsetX, offsetY, 0)));
            } else {
              camera.setTarget(originalTarget);
              this.scene.onBeforeRenderObservable.remove(shakeObs);
            }
          });
        }
      }),
    );

    // Co-op — indigo shimmer when Chronos Bubble opens.
    this.unsubs.push(
      gameEvents.on(GameEvents.CHRONOS_BUBBLE_TRIGGERED, (p) => {
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        // Shimmering time-bubble particle field
        this.burst({
          emitter: new Vector3(p.x, p.y, p.z),
          count: 100,
          durationSec: 0.95,
          color1: a11yColor4FromRgb('BIOLUMINESCENT_PURPLE', 0.3, 0.2, 0.9, 0.9, rc),
          color2: a11yColor4FromRgb('BIOLUMINESCENT_BLUE', 0.1, 0.5, 0.9, 0.75, rc),
          colorDead: new Color4(0.02, 0.02, 0.15, 0),
          minSize: 0.08,
          maxSize: 0.24,
          minLifeTime: 0.9,
          maxLifeTime: 1.8,
          minEmitPower: 0.4,
          maxEmitPower: 1.5,
          direction1: new Vector3(-1.5, -0.8, -1.5),
          direction2: new Vector3(1.5, 1.5, 1.5),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 200,
        });
        this.pulseLight(
          new Vector3(p.x, p.y + 0.3, p.z),
          a11yColor4FromRgb('BIOLUMINESCENT_PURPLE', 0.3, 0.2, 0.9, 0.9, rc),
          3.0,
          11.0,
          850,
        );

        // Visual Chronos Sphere (PBR glowing dome mesh scaling up)
        // Omitted when reducedMotion is enabled — purely decorative large visual.
        const reducedForSphere = AccessibilitySystem.getInstance().state.reducedMotion;
        if (this.visualConfig.chronosSphere !== false && !reducedForSphere && this.scene.getEngine().name !== "NullEngine" && typeof MeshBuilder !== "undefined") {
          try {
            const sphere = MeshBuilder.CreateSphere(
              "chronosVisualSphere",
              { diameter: 1.0, segments: 16 },
              this.scene
            );
            sphere.position.set(p.x, p.y, p.z);
            sphere.isPickable = false;

            const mat = new PBRMaterial("chronosVisualMat", this.scene);
            mat.metallic = 0.0;
            mat.roughness = 0.2;
            mat.alpha = 0.12;
            mat.albedoColor = new Color3(0.2, 0.4, 0.9);
            mat.emissiveColor = new Color3(0.1, 0.3, 0.8);
            sphere.material = mat;

            const maxRadius = p.radius ?? 15;
            let elapsed = 0;
            const animDuration = 600; // ms to reach full size
            const totalDuration = 5000; // ms

            let sphereObs: ReturnType<typeof this.scene.onBeforeRenderObservable.add> = null;
            const cleanup = () => {
              if (!this.transientCleanups.delete(cleanup)) return;
              this.scene.onBeforeRenderObservable.remove(sphereObs);
              sphere.dispose(false, true);
            };
            this.transientCleanups.add(cleanup);
            sphereObs = this.scene.onBeforeRenderObservable.add(() => {
              const dt = this.scene.getEngine().getDeltaTime();
              elapsed += dt;

              if (elapsed < animDuration) {
                const ratio = elapsed / animDuration;
                // Ease-out quad scale
                const scale = maxRadius * (2 * ratio - ratio * ratio);
                sphere.scaling.set(scale, scale, scale);
              } else if (elapsed < totalDuration) {
                // Keep scaled and fade out at the end
                const remaining = totalDuration - elapsed;
                if (remaining < 1000) {
                  mat.alpha = 0.12 * (remaining / 1000);
                }
              } else {
                cleanup();
              }
            });
          } catch (e) {
            console.warn("[AbilityVFX] Chronos Visual Sphere failed:", e);
          }
        }
      }),
    );

    // Tactile locomotion juice — subtle paw-level dust puffs on jump takeoff and landing.
    this.unsubs.push(
      gameEvents.on(GameEvents.PLAYER_JUMPED, (who) => {
        const mesh = who === "basil" ? this.basil : this.minna;
        if (!mesh?.position) return;
        const emitter = new Vector3(mesh.position.x, mesh.position.y - 0.32, mesh.position.z);
        const isBasil = who === "basil";
        this.burst({
          emitter,
          count: isBasil ? 14 : 10,
          durationSec: 0.1,
          color1: isBasil
            ? new Color4(0.88, 0.72, 0.52, 0.55)
            : new Color4(0.55, 0.88, 0.72, 0.48),
          color2: new Color4(0.62, 0.56, 0.48, 0.35),
          colorDead: new Color4(0.25, 0.22, 0.2, 0),
          minSize: 0.04,
          maxSize: 0.11,
          minLifeTime: 0.18,
          maxLifeTime: 0.34,
          minEmitPower: 0.8,
          maxEmitPower: 1.9,
          direction1: new Vector3(-1.1, 0.15, -0.35),
          direction2: new Vector3(1.1, 0.65, 0.35),
          gravity: new Vector3(0, -1.5, 0),
          blendMode: ParticleSystem.BLENDMODE_STANDARD,
          emitRate: 140,
        });
      }),
    );

    this.unsubs.push(
      gameEvents.on(GameEvents.PLAYER_LANDED, (who) => {
        const mesh = who === "basil" ? this.basil : this.minna;
        if (!mesh?.position) return;
        const emitter = new Vector3(mesh.position.x, mesh.position.y - 0.34, mesh.position.z);
        const isBasil = who === "basil";
        this.burst({
          emitter,
          count: isBasil ? 18 : 12,
          durationSec: 0.12,
          color1: isBasil
            ? new Color4(0.85, 0.68, 0.46, 0.62)
            : new Color4(0.68, 0.78, 0.74, 0.5),
          color2: new Color4(0.55, 0.5, 0.44, 0.38),
          colorDead: new Color4(0.22, 0.2, 0.18, 0),
          minSize: 0.05,
          maxSize: 0.13,
          minLifeTime: 0.22,
          maxLifeTime: 0.42,
          minEmitPower: 1.1,
          maxEmitPower: 2.4,
          direction1: new Vector3(-1.5, 0.08, -0.4),
          direction2: new Vector3(1.5, 0.45, 0.4),
          gravity: new Vector3(0, -2.0, 0),
          blendMode: ParticleSystem.BLENDMODE_STANDARD,
          emitRate: 160,
        });
      }),
    );

    // Co-op bond ("Unravel Two" proximity resonance) — intertwined emerald + brass
    // shimmer at the midpoint between Minna and Basil when Co-Regulation activates.
    this.unsubs.push(
      gameEvents.on(GameEvents.COREGULATION_ACTIVE, () => {
        if (!this.minna?.position || !this.basil?.position) return;
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        const mid = Vector3.Center(this.minna.position, this.basil.position);
        mid.y += 0.25;
        this.burst({
          emitter: mid,
          count: 24,
          durationSec: 0.35,
          color1: a11yColor4FromRgb("EMERALD", 0.18, 0.95, 0.58, 0.85, rc),
          color2: a11yColor4FromRgb("BRASS", 0.98, 0.78, 0.36, 0.85, rc),
          colorDead: new Color4(0.1, 0.25, 0.2, 0),
          minSize: 0.05,
          maxSize: 0.14,
          minLifeTime: 0.4,
          maxLifeTime: 0.75,
          minEmitPower: 0.6,
          maxEmitPower: 1.5,
          direction1: new Vector3(-1.0, 0.3, -0.5),
          direction2: new Vector3(1.0, 1.2, 0.5),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 120,
        });
      }),
    );

    // Puzzle triumph — celebratory upward shower of warm brass + bioluminescent
    // emerald sparks above both cats when a puzzle is solved.
    this.unsubs.push(
      gameEvents.on(GameEvents.PUZZLE_SOLVED, () => {
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        for (const [mesh, isBasil] of [
          [this.minna, false],
          [this.basil, true],
        ] as const) {
          if (!mesh?.position) continue;
          this.burst({
            emitter: new Vector3(mesh.position.x, mesh.position.y + 0.45, mesh.position.z),
            count: 28,
            durationSec: 0.28,
            color1: isBasil
              ? a11yColor4FromRgb("BRASS", 1.0, 0.84, 0.38, 0.95, rc)
              : a11yColor4FromRgb("EMERALD", 0.2, 1.0, 0.62, 0.95, rc),
            color2: a11yColor4FromRgb("BIOLUMINESCENT_BLUE", 0.45, 0.92, 1.0, 0.8, rc),
            colorDead: new Color4(0.15, 0.25, 0.2, 0),
            minSize: 0.06,
            maxSize: 0.16,
            minLifeTime: 0.4,
            maxLifeTime: 0.8,
            minEmitPower: 1.8,
            maxEmitPower: 3.6,
            direction1: new Vector3(-1.2, 1.0, -0.6),
            direction2: new Vector3(1.2, 2.4, 0.6),
            gravity: new Vector3(0, -3.5, 0),
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitRate: 180,
          });
        }
      }),
    );

    // Wounds/Medicine restoration — warm copper-gold mend shimmer when Basil's harness heals.
    this.unsubs.push(
      gameEvents.on(GameEvents.HARNESS_HEALED, () => {
        if (!this.basil?.position) return;
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        this.burst({
          emitter: new Vector3(this.basil.position.x, this.basil.position.y + 0.15, this.basil.position.z),
          count: 26,
          durationSec: 0.25,
          color1: a11yColor4FromRgb("BRASS", 1.0, 0.86, 0.42, 0.9, rc),
          color2: a11yColor4FromRgb("COPPER", 0.95, 0.58, 0.24, 0.8, rc),
          colorDead: new Color4(0.25, 0.15, 0.05, 0),
          minSize: 0.05,
          maxSize: 0.14,
          minLifeTime: 0.35,
          maxLifeTime: 0.65,
          minEmitPower: 1.0,
          maxEmitPower: 2.2,
          direction1: new Vector3(-0.9, 0.5, -0.5),
          direction2: new Vector3(0.9, 1.6, 0.5),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 160,
        });
        this.pulseLight(
          new Vector3(this.basil.position.x, this.basil.position.y + 0.25, this.basil.position.z),
          a11yColor4FromRgb("BRASS", 1.0, 0.86, 0.42, 0.9, rc),
          2.0,
          4.5,
          320,
        );
      }),
    );

    // Tactile blocked-ability feedback — subtle 3D smoke fizzle at the cat's position
    // when an ability press is rejected (cooldown, energy, range, or grounded gate).
    this.unsubs.push(
      gameEvents.on(GameEvents.ABILITY_BLOCKED, ({ ownerId }) => {
        const mesh = ownerId === "basil" ? this.basil : this.minna;
        if (!mesh?.position) return;
        this.burst({
          emitter: new Vector3(mesh.position.x, mesh.position.y + 0.2, mesh.position.z),
          count: 8,
          durationSec: 0.12,
          color1: new Color4(0.55, 0.52, 0.5, 0.55),
          color2: new Color4(0.35, 0.32, 0.34, 0.35),
          colorDead: new Color4(0.15, 0.14, 0.16, 0),
          minSize: 0.04,
          maxSize: 0.09,
          minLifeTime: 0.16,
          maxLifeTime: 0.28,
          minEmitPower: 0.4,
          maxEmitPower: 1.0,
          direction1: new Vector3(-0.6, 0.2, -0.3),
          direction2: new Vector3(0.6, 0.9, 0.3),
          blendMode: ParticleSystem.BLENDMODE_STANDARD,
          emitRate: 90,
        });
      }),
    );
  }

  /**
   * Spawn a short-lived dynamic PointLight pulse at the ability origin so nearby
   * PBR surfaces (brass gears, stone floors, cat fur) catch the ability's glow.
   * Skipped when reducedMotion is enabled; capped at 4 concurrent pulse lights.
   */
  private pulseLight(
    position: Vector3,
    color: Color4,
    peakIntensity: number,
    range: number,
    durationMs: number,
    source?: AbstractMesh,
  ): void {
    if (this.disposed) return;
    if (AccessibilitySystem.getInstance().state.reducedMotion) return;

    while (this.activeLights.length >= 4) {
      const oldest = this.activeLights.shift();
      oldest?.cleanup();
    }

    const light = new PointLight(`vfx_light_${Date.now()}_${this.activeLights.length}`, position.clone(), this.scene);
    light.diffuse = new Color3(color.r, color.g, color.b);
    light.specular = new Color3(color.r, color.g, color.b);
    light.intensity = peakIntensity;
    light.range = range;
    // This unshadowed point is inside its source cat. It may illuminate the
    // world and partner, but must not bleach its own coat through the body.
    if (source) light.excludedMeshes = [source, ...source.getChildMeshes()];

    let cleaned = false;
    let obs: ReturnType<typeof this.scene.onBeforeRenderObservable.add> = null;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      if (obs) {
        this.scene.onBeforeRenderObservable.remove(obs);
        obs = null;
      }
      try {
        light.dispose();
        light.excludedMeshes.length = 0;
      } catch {
        /* ignore */
      }
      const idx = this.activeLights.findIndex((e) => e.light === light);
      if (idx >= 0) this.activeLights.splice(idx, 1);
    };

    const entry = { light, cleanup };
    this.activeLights.push(entry);

    if (this.scene.getEngine().name !== "NullEngine") {
      let elapsed = 0;
      obs = this.scene.onBeforeRenderObservable.add(() => {
        elapsed += this.scene.getEngine().getDeltaTime();
        if (elapsed >= durationMs) {
          cleanup();
          return;
        }
        const t = Math.max(0, Math.min(1, elapsed / durationMs));
        const rem = 1 - t;
        light.intensity = peakIntensity * rem * rem;
      });
    }
  }

  /** Spawn a one-shot particle system and auto-dispose when its lifetime ends. */
  private burst(spec: BurstSpec): void {
    if (this.disposed) return;
    const reduced = AccessibilitySystem.getInstance().state.reducedMotion;
    const factor = particleDensityFactor(reduced);
    const density = this.visualConfig.particleDensityScale ?? 1;
    const size = this.visualConfig.particleSizeScale ?? 1;
    const capacity = Math.max(1, Math.ceil(spec.count * factor * density));
    const ps = new ParticleSystem(`vfx_burst_${this.pool.length}`, capacity, this.scene);
    ps.particleTexture = this.sprite;
    ps.emitter = spec.emitter.clone();
    ps.minEmitBox = new Vector3(-0.05, -0.05, -0.05);
    ps.maxEmitBox = new Vector3(0.05, 0.05, 0.05);
    ps.color1 = spec.color1;
    ps.color2 = spec.color2;
    ps.colorDead = spec.colorDead;
    ps.minSize = spec.minSize * size;
    ps.maxSize = spec.maxSize * size;
    ps.minLifeTime = spec.minLifeTime;
    ps.maxLifeTime = spec.maxLifeTime;
    ps.emitRate = spec.emitRate * factor * density;
    ps.minEmitPower = spec.minEmitPower;
    ps.maxEmitPower = spec.maxEmitPower;
    ps.direction1 = spec.direction1;
    ps.direction2 = spec.direction2;
    if (spec.gravity) ps.gravity = spec.gravity;
    ps.blendMode = spec.blendMode ?? ParticleSystem.BLENDMODE_STANDARD;
    // The emitter stops after `durationSec`; remaining in-flight particles
    // finish their lifetime and the system self-disposes after that.
    ps.targetStopDuration = spec.durationSec;
    // Babylon's disposeOnStop uses dispose(true), which would retire the
    // sprite shared by all active and future bursts. The VFX owner retains
    // that texture until it leaves; an empty stopped burst owns only itself.
    ps.disposeOnStop = false;
    ps.onAnimationEnd = () => ps.dispose(false);
    ps.onDisposeObservable.addOnce(() => {
      ps.onAnimationEnd = null;
      const index = this.pool.indexOf(ps);
      if (index >= 0) this.pool.splice(index, 1);
    });
    this.pool.push(ps);
    ps.start();
  }

  dispose(): void {
    this.disposed = true;
    for (const unsub of this.unsubs) unsub();
    this.unsubs.length = 0;
    for (const ps of [...this.pool]) {
      try { ps.dispose(false); } catch { /* ignore */ }
    }
    this.pool.length = 0;
    for (const item of [...this.activeLights]) {
      item.cleanup();
    }
    this.activeLights.length = 0;
    for (const cleanup of [...this.transientCleanups]) cleanup();
    this.sprite.dispose();
  }
}
